import { ACCOUNTING, TREASURY } from '@nebula-shared/data-access';
import { BuscadorMaestroConfig } from './buscador-maestro.models';

/** Registro del page-response de terceros, tal como lo pinta y lo devuelve la ventana. */
export type TerceroDeBuscador = Record<string, unknown>;

/** Lo que cambia de una lupa de terceros a otra; lo demas es igual en todo el ERP. */
export interface OpcionesBuscadorTerceros {
  /** Clave i18n del titulo de la ventana. */
  tituloKey: string;
  /** Ruta relativa del page-response de terceros (`TercerosService.rutaDePageResponse`). */
  endpoint: string;
  /** Agrega la columna del tipo de persona (documento 30 %, nombre 50 %, tipo 20 %). */
  conTipoPersona?: boolean;
  /** Rotulos i18n propios de la pantalla para el documento y el nombre. */
  rotulos?: { documento?: string; nombre?: string };
  /** El tercero que el campo ya tiene: su fila sale marcada (por `id` salvo que se diga otra clave). */
  valorActual?: BuscadorMaestroConfig<TerceroDeBuscador>['valorActual'];
}

/**
 * El documento del tercero con su digito de verificacion (`numeroDocumento-dv`), como lo
 * mostraba el selector anterior. Sin DV, el documento solo. El DV 0 es valido y se muestra.
 *
 * @param tercero registro con `numeroDocumento` y `dv`.
 * @returns el documento para mostrar.
 */
export function documentoConDv(tercero: { numeroDocumento?: unknown; dv?: unknown }): string {
  const documento = String(tercero.numeroDocumento ?? '');
  const dv = tercero.dv;
  return dv === null || dv === undefined || dv === '' ? documento : `${documento}-${dv}`;
}

/**
 * El nombre que se muestra del tercero, como lo hacia el selector anterior: para persona juridica
 * con razon social, la razon social; en otro caso, `nombreCompleto` del servidor.
 *
 * @param tercero registro con `tipoPersona`, `razonSocial` y `nombreCompleto`.
 * @returns el nombre para mostrar.
 */
export function nombreDeTercero(tercero: {
  tipoPersona?: unknown;
  razonSocial?: unknown;
  nombreCompleto?: unknown;
}): unknown {
  const esJuridica = String(tercero.tipoPersona ?? '')
    .toLowerCase()
    .includes('juridica');
  return esJuridica && tercero.razonSocial ? tercero.razonSocial : tercero.nombreCompleto;
}

/**
 * Configuracion unica de la lupa de terceros activos sobre `BuscadorMaestroService.abrir`: el
 * documento con su DV, el nombre (la razon social si es persona juridica, `nombreCompleto` si no),
 * el filtro fijo de terceros activos y el orden por documento. Lo elegido se devuelve tal como
 * llego del servidor (sin el DV pegado al documento).
 *
 * @param opciones titulo, endpoint y lo propio de la pantalla.
 * @returns la configuracion de la ventana.
 */
export function buscadorDeTerceros(
  opciones: OpcionesBuscadorTerceros,
): BuscadorMaestroConfig<TerceroDeBuscador> {
  const tipo = !!opciones.conTipoPersona;
  return {
    tituloKey: opciones.tituloKey,
    endpoint: opciones.endpoint,
    idKey: 'id',
    columnas: [
      {
        key: 'numeroDocumento',
        labelKey: opciones.rotulos?.documento ?? TREASURY.PARAMETERS.FIELDS.IDENTIFICATION.LABEL,
        sortable: true,
        width: tipo ? '30%' : '40%',
      },
      {
        key: 'nombreCompleto',
        labelKey:
          opciones.rotulos?.nombre ??
          ACCOUNTING.ENTRY_DEFERRED.DEFERRED_MONTH_TABLE.COLUMN.NAME_OR_BUSINESS_NAME,
        sortable: true,
        width: tipo ? '50%' : '60%',
      },
      ...(tipo
        ? [
            {
              key: 'tipoPersona',
              labelKey: ACCOUNTING.THIRD_PARTY.FORM.PERSON_TYPE,
              sortable: true,
              width: '20%',
            },
          ]
        : []),
    ],
    base: {
      searchsBy: [{ key: 'estadoTercero', operation: 'EQUAL', value: 'ACTIVO' }],
      ordersBy: [{ column: 'numeroDocumento', direction: 'ASC' }],
    },
    presentar: (tercero) => ({
      ...tercero,
      numeroDocumento: documentoConDv(tercero),
      nombreCompleto: nombreDeTercero(tercero),
    }),
    ...(opciones.valorActual ? { valorActual: opciones.valorActual } : {}),
  };
}
